<template>
  <div>
    <data-mapper
      v-if="hasColumns"
      :columns="model.config.columns"
      :attributes-prop="attributesProp"
      :filterable-attributes="filterableAttributes"
      :grouped-filterable-attributes="groupedFilterableAttributes"
      :fetch-attributes="fetchAttributes"
      :fetch-preferences="isConsentPreferencesEnabled"
      :include-metadata="isConsentPreferencesEnabled"
      :fixed-attribute="fixedAttribute"
      :show-unrecognized-error="shouldShowUnrecognizedError"
      :unrecognized-error-message="unrecognizedErrorMessage"
      :errors="errorMessages"
    />
  </div>
</template>

<script>
import DataMapper from '@/common/components/DataMapper'
import { $hasFeature } from '@/core/utils/VueAcl'

export default {
  name: 'StepImportDataMapping',

  components: {
    DataMapper
  },

  props: {
    steps: {
      type: Array,
      required: true
    },

    model: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      uploadProps: ['file_id'],
      fsProps: ['integration_id', 'file_directory', 'file_name'],
      errorMessages: []
    }
  },

  computed: {
    hasColumns() {
      return this.model.config.hasOwnProperty('columns')
    },

    attributesProp() {
      if (this.model.type === 'profile_attributes') {
        return []
      }
      return this.model.type !== 'events' ||
        this.model.config.event === 'purchase'
        ? this.steps[1].props.attributes
        : []
    },

    filterableAttributes() {
      return this.steps[1].props.filterableAttributes
    },

    groupedFilterableAttributes() {
      return this.steps[1].props.groupedFilterableAttributes
    },

    fetchAttributes() {
      if (this.model.type === 'profile_attributes') {
        return false
      }
      return (
        this.model.type !== 'events' || this.model.config.event === 'purchase'
      )
    },

    fixedAttribute() {
      return this.model.type === 'events' &&
        this.model.config.event !== 'purchase'
        ? 'Import'
        : ''
    },

    shouldShowUnrecognizedError() {
      return (
        this.model.type !== 'events' && this.model.type !== 'profile_attributes'
      )
    },

    isUserAttributeColumnsCompleted() {
      if (this.model.type !== 'profile_attributes') {
        return true
      }
      return (
        this.model.config.columns.some(e => e.attribute === 'attribute_name') &&
        this.model.config.columns.some(e => e.attribute === 'attribute_value')
      )
    },

    unrecognizedErrorMessage() {
      return 'Imports_Text_AttributeNotRecognized'
    },

    isUserProfileAttributesCompleted() {
      if ($hasFeature('profileRequireUserId')) {
        return this.model.config.columns.some(e => e.attribute === 'user_id')
      }

      return this.model.config.columns.some(
        e =>
          e.attribute === 'user_id' ||
          (e.attribute === 'customer_id' && $hasFeature('customerIdEnabled')) ||
          (e.attribute === 'email' && $hasFeature('profileIdfybyEmail'))
      )
    },

    isEventAttributesCompleted() {
      if (this.model.type === 'events') {
        return this.model.config.columns.some(e => e.attribute === 'timestamp')
      }

      return true
    },

    isConsentPreferencesEnabled() {
      return $hasFeature('subscriptionPreferences')
    },

    userIdErrorMessage() {
      const requireUserId = $hasFeature('profileRequireUserId')
      const customerIdEnabled =
        !requireUserId && $hasFeature('customerIdEnabled')
      const emailEnabled = !requireUserId && $hasFeature('profileIdfybyEmail')

      if (customerIdEnabled && emailEnabled) {
        return 'Imports_Message_IdRequired_UserIdOrCustomerIdOrEmail'
      }
      if (customerIdEnabled) {
        return 'Imports_Message_IdRequired_UserIdOrCustomerId'
      }
      if (emailEnabled) {
        return 'Imports_Message_IdRequired_UserIdOrEmail'
      }
      return 'Imports_Message_IdRequired_UserId'
    }
  },

  mounted() {
    this.clearModelForType()
    this.toggleStep()

    if (this.isUserProfileAttributesCompleted === false) {
      this.errorMessages.push(this.userIdErrorMessage)
    }

    if (this.isEventAttributesCompleted === false) {
      this.errorMessages.push('Imports_Message_EventColsNotAssigned')
    }

    if (this.isUserAttributeColumnsCompleted === false) {
      this.errorMessages.push('Imports_Message_UserAttributeColsNotAssigned')
    }
  },

  methods: {
    clearModelForType() {
      switch (this.model.source.type) {
        case 'upload':
          this.clearModelSchedule()
          this.clearModelSource(this.uploadProps)
          break
        case 'fs':
          this.clearModelSource(this.fsProps)
          break
        case 'db':
          this.clearModelSource([
            'integration_id',
            'query',
            'sync_mode',
            'delta_column',
            'row_limit'
          ])
          break
      }
    },

    clearModelSource(propsArray) {
      for (const key in this.model.source) {
        if (!propsArray.includes(key) && key !== 'type') {
          this.$delete(this.model.source, key)
        }
      }
    },

    clearModelSchedule() {
      if (this.model.hasOwnProperty('schedule')) {
        this.$delete(this.model, 'schedule')
      }
    },

    handleErrorMessage(condition, messageKey) {
      if (condition === false && !this.errorMessages.includes(messageKey)) {
        this.errorMessages.push(messageKey)
      } else if (condition === true) {
        const index = this.errorMessages.indexOf(messageKey)
        if (index !== -1) {
          this.errorMessages.splice(index, 1)
        }
      }
    },

    toggleStep() {
      this.$emit('can-continue', {
        value:
          this.isUserProfileAttributesCompleted &&
          this.isEventAttributesCompleted &&
          this.isUserAttributeColumnsCompleted
      })
    }
  },

  watch: {
    isUserProfileAttributesCompleted(newValue) {
      this.toggleStep()
      this.handleErrorMessage(newValue, this.userIdErrorMessage)
    },

    isEventAttributesCompleted(newValue) {
      this.toggleStep()
      this.handleErrorMessage(newValue, 'Imports_Message_EventColsNotAssigned')
    },

    isUserAttributeColumnsCompleted(newValue) {
      this.toggleStep()
      this.handleErrorMessage(
        newValue,
        'Imports_Message_UserAttributeColsNotAssigned'
      )
    }
  }
}
</script>

<style scoped>
.error-field {
  font-size: 12px;
  line-height: 2;
  padding-top: 4px;
}
</style>



// WEBPACK FOOTER //
// src/modules/imports/components/StepImportDataMapping.vue